import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNotify, usePermissions, useTranslate } from "ra-core";
import { useState } from "react";
import { Confirm } from "@/components/admin/confirm";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { formatDateTime } from "../../domain/format";
import { useM365Api } from "../../integrations/api/M365ApiContext";
import { errorMessage } from "../../integrations/api/errorMessage";
import { M365ApiError } from "../../integrations/api/types";
import {
  isM365NotConfigured,
  m365QueryRetry,
} from "../../integrations/api/m365Retry";
import { isMicrosoftLoginUrl } from "./microsoftUrl";

export const MS_STATUS_KEY = ["gavion", "m365", "status"] as const;

// GAVION batch 3 (Task 20): every M365 function answers m365_not_configured until the Entra app exists.
const isNotConfigured = isM365NotConfigured;

export const MicrosoftCard = () => {
  const t = useTranslate();
  const notify = useNotify();
  const api = useM365Api();
  const qc = useQueryClient();
  const [confirming, setConfirming] = useState(false);
  const { data: s, error } = useQuery({
    queryKey: MS_STATUS_KEY,
    queryFn: () => api.microsoftStatus(),
    // Retrying cannot help until an administrator configures Microsoft 365.
    retry: m365QueryRetry,
  });
  const notConfigured = isNotConfigured(error);
  const { permissions } = usePermissions();
  // GAVION full-email: the archive permission matters to admins only (the mailbox archive card)
  const missing = (s?.missing ?? []).filter(
    (f) => f !== "archive" || permissions === "admin",
  );
  const connect = async () => {
    try {
      const { url } = await api.startMicrosoftConnect();
      if (!isMicrosoftLoginUrl(url)) throw new M365ApiError("connect_failed");
      window.location.href = url;
    } catch (e) {
      notify(errorMessage(t, e), { type: "error" });
    }
  };
  const disconnect = async () => {
    try {
      await api.disconnectMicrosoft();
      setConfirming(false);
      notify("gavion.integrations.microsoft.disconnected_toast", {
        type: "info",
      });
      await qc.invalidateQueries({ queryKey: MS_STATUS_KEY });
    } catch (e) {
      notify(errorMessage(t, e), { type: "error" });
    }
  };
  const status = s?.status ?? "none";
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-2">
          <CardTitle>{t("gavion.integrations.microsoft.title")}</CardTitle>
          <Badge variant={status === "connected" ? "default" : "secondary"}>
            {t(`gavion.integrations.microsoft.status.${status}`)}
          </Badge>
        </div>
        <CardDescription>
          {t("gavion.integrations.microsoft.description")}
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-3 text-sm">
        {notConfigured && (
          <p role="alert">
            {t("gavion.integrations.errors.m365_not_configured")}
          </p>
        )}
        {s?.externalEmail && status === "connected" && (
          <p>
            {t("gavion.integrations.microsoft.connected_as", {
              email: s.externalEmail,
            })}
          </p>
        )}
        {status === "connected" && (
          <p className="text-muted-foreground">
            {s?.lastSyncAt
              ? t("gavion.integrations.microsoft.last_sync", {
                  at: formatDateTime(s.lastSyncAt),
                })
              : t("gavion.integrations.microsoft.never_synced")}
          </p>
        )}
        {status === "needs_reauth" && (
          <p role="alert">
            {t("gavion.integrations.microsoft.needs_reauth_help")}
          </p>
        )}
        {status === "connected" && missing.length > 0 && (
          <div>
            <p className="font-semibold">
              {t("gavion.integrations.microsoft.missing_title")}
            </p>
            <ul className="list-disc pl-5">
              {missing.map((f) => (
                <li key={f}>
                  {t(`gavion.integrations.microsoft.features.${f}`)}
                </li>
              ))}
            </ul>
            {s?.adminConsentUrl && (
              <a
                className="underline"
                href={s.adminConsentUrl}
                target="_blank"
                rel="noreferrer noopener"
              >
                {t("gavion.integrations.microsoft.ask_admin")}
              </a>
            )}
          </div>
        )}
        <div className="flex gap-2">
          {status === "connected" ? (
            <Button variant="outline" onClick={() => setConfirming(true)}>
              {t("gavion.integrations.microsoft.disconnect")}
            </Button>
          ) : notConfigured ? null : (
            <Button onClick={connect}>
              {t(
                status === "needs_reauth"
                  ? "gavion.integrations.microsoft.reconnect"
                  : "gavion.integrations.microsoft.connect",
              )}
            </Button>
          )}
        </div>
      </CardContent>
      <Confirm
        isOpen={confirming}
        title="gavion.integrations.microsoft.confirm_disconnect_title"
        content="gavion.integrations.microsoft.confirm_disconnect_body"
        confirm="gavion.integrations.microsoft.disconnect"
        onConfirm={disconnect}
        onClose={() => setConfirming(false)}
      />
    </Card>
  );
};
